import React from 'react';
import ReplyList from './reply-list';
import { CommentStateSwitch } from './comment-state-switch';
import { LoadMoreStateSwitch } from './load-more-state-switch';
import { useScrollAnchoring } from './scrollAnchoring';
import { useHighlightComment } from './use-highlight-comment';
import { LOAD_MORE_PREV, LOAD_MORE_NEXT, TOP_LEVEL_COMMENT_LIST, SHOW_LIVE_COMMENTS_END, SHOW_LIVE_COMMENTS_START, } from '../../../common/constants/data-hooks';
import { assertDefined, unreachable } from '../../../ts-utils';
import { useConfiguration } from '../../context/configuration-context/use-configuration';
import { ShowLiveComments } from '../live-updates-components';
import { useApi } from '../../context/api-context/use-api';
import { useTranslate } from '../../context/api-context/translations/use-translate';
import { makeCommentInnerListId } from './id-utils';
import { isMarkable } from '../../../common/store/comments/mark/mark-types';
import { getMarkedState } from '../../../common/store/comments/mark/mark-selectors';
import ReactionConfigProvider from '../../context/configuration-context/reaction-config/reaction-config-provider';
import { Divider } from 'wix-ui-tpa';
import { st, classes } from './comment-list.st.css';
import { getCurrentOwnerId, getOwner, getOwnerId, } from '../../../common/store/members/members-selectors';
export const CommentList = React.forwardRef(({ fetchNextComments, fetchPrevComments, fetchNextReplies, fetchPrevReplies, state, appPermissionState, isMobile, liveCommentsLayout, }, ref) => {
    const { reactions, screenshotMode, resourceId, ratings: withRatings } = useConfiguration();
    const t = useTranslate();
    const reactionsConfig = reactions && reactions.commentReactions;
    useScrollAnchoring(state.scrollAnchor);
    useHighlightComment(state.highlight, isMobile, screenshotMode);
    const { revealLiveComments, showLiveCommentsButtonDev, showLoadMoreCommentsDev, currentOwnerId, getCommentOwner, } = useApi((state, actions, _host, ctx) => ({
        revealLiveComments: actions.revealLiveComments,
        showLiveCommentsButtonDev: ctx.wcDevToolsState.showLiveCommentsButton,
        showLoadMoreCommentsDev: ctx.wcDevToolsState.showLoadMoreComments,
        currentOwnerId: getCurrentOwnerId(state.members),
        getCommentOwner: (commentState) => getOwner(commentState, state.members),
    }));
    return (React.createElement(ReactionConfigProvider, { config: reactionsConfig },
        React.createElement("div", { className: st(classes.root, {
                isMobile,
                isEmpty: state.isEmpty,
                isLastComponent: liveCommentsLayout === 'TOP',
            }), "data-hook": TOP_LEVEL_COMMENT_LIST },
            (() => {
                switch (showLoadMoreCommentsDev) {
                    case 'auto':
                        return (React.createElement(LoadMoreStateSwitch, { textKey: "comment-list.view-more-comments", onClick: fetchPrevComments, state: state.prev, className: classes.loadMorePrev, dataHook: LOAD_MORE_PREV }));
                    case 'on':
                    case 'on-spinner':
                        return (React.createElement(LoadMoreStateSwitch, { textKey: "comment-list.view-more-comments", onClick: () => { }, state: showLoadMoreCommentsDev === 'on-spinner'
                                ? { type: 'PENDING', count: 10 }
                                : { type: 'HAS', cursor: '', count: 10 }, className: classes.loadMorePrev, dataHook: LOAD_MORE_PREV }));
                    case 'off':
                        return null;
                    default:
                        throw unreachable(showLoadMoreCommentsDev);
                }
            })(),
            (() => {
                if (liveCommentsLayout === 'BOTTOM') {
                    return;
                }
                switch (showLiveCommentsButtonDev) {
                    case 'auto':
                        return state.showLiveCommentsButton.type === 'VISIBLE' ? (React.createElement(ShowLiveComments, { text: t('live-updates.show-live-comments', {
                                count: state.showLiveCommentsButton.count,
                            }), dataHook: SHOW_LIVE_COMMENTS_START, onClick: () => revealLiveComments({ resourceId, position: 'start' }), position: "start" })) : null;
                    case 'on':
                        return (React.createElement(ShowLiveComments, { text: t('live-updates.show-live-comments', { count: 1 }), dataHook: SHOW_LIVE_COMMENTS_START, onClick: () => { }, position: "start" }));
                    case 'off':
                        return null;
                    default:
                        throw unreachable(showLiveCommentsButtonDev);
                }
            })(),
            React.createElement("div", { ref: ref, id: makeCommentInnerListId(resourceId), className: classes.commentListInner }, state.commentList.map((commentState, index) => {
                const repliesState = 'commentId' in commentState ? state.replies[commentState.commentId] : undefined;
                const key = 'localId' in commentState
                    ? typeof commentState.localId === 'undefined' && 'commentId' in commentState
                        ? commentState.commentId
                        : commentState.localId
                    : commentState.commentId;
                assertDefined(key);
                const commentByCurrentOwner = currentOwnerId === getOwnerId(getCommentOwner(commentState));
                return (React.createElement(React.Fragment, { key: key },
                    resolveDivider(index, state.commentList, isMobile),
                    React.createElement(CommentStateSwitch, { commentState: commentState, appPermissionState: appPermissionState, currentRating: withRatings && commentByCurrentOwner ? state.ratings.currentRating : undefined, inputRating: withRatings && commentByCurrentOwner ? state.ratings.inputRating : undefined, isByCurrentOwner: commentByCurrentOwner }),
                    repliesState && (React.createElement(ReplyList, { state: repliesState, fetchNextReplies: () => 'commentId' in commentState && fetchNextReplies(commentState.commentId), fetchPrevReplies: () => 'commentId' in commentState && fetchPrevReplies(commentState.commentId), appPermissionState: appPermissionState, id: `reply-list-${key}` }))));
            })),
            (() => {
                if (liveCommentsLayout === 'TOP') {
                    return null;
                }
                switch (showLiveCommentsButtonDev) {
                    case 'auto':
                        return state.showLiveCommentsButton.type === 'VISIBLE' ? (React.createElement(ShowLiveComments, { text: t('live-updates.show-live-comments', {
                                count: state.showLiveCommentsButton.count,
                            }), dataHook: SHOW_LIVE_COMMENTS_END, onClick: () => revealLiveComments({ resourceId, position: 'end' }), position: "end" })) : null;
                    case 'on':
                        return (React.createElement(ShowLiveComments, { text: t('live-updates.show-live-comments', { count: 1 }), dataHook: SHOW_LIVE_COMMENTS_END, onClick: () => { }, position: "end" }));
                    case 'off':
                        return null;
                    default:
                        throw unreachable(showLiveCommentsButtonDev);
                }
            })(),
            (() => {
                switch (showLoadMoreCommentsDev) {
                    case 'auto':
                        return (React.createElement(LoadMoreStateSwitch, { textKey: "comment-list.view-more-comments", onClick: fetchNextComments, state: state.next, className: classes.loadMoreNext, dataHook: LOAD_MORE_NEXT }));
                    case 'on':
                    case 'on-spinner':
                        return (React.createElement(LoadMoreStateSwitch, { textKey: "comment-list.view-more-comments", onClick: () => { }, state: showLoadMoreCommentsDev === 'on-spinner'
                                ? { type: 'PENDING', count: 10 }
                                : { type: 'HAS', cursor: '', count: 10 }, className: classes.loadMoreNext, dataHook: LOAD_MORE_NEXT }));
                    case 'off':
                        return null;
                    default:
                        throw unreachable(showLoadMoreCommentsDev);
                }
            })())));
});
const resolveDivider = (index, commentList, isMobile) => {
    if (index === 0) {
        return null;
    }
    const next = commentList[index];
    const prev = commentList[index - 1];
    const nextIsMarked = isMarkable(next) && getMarkedState(next.comment, next.marked).isMarked;
    const prevIsMarked = isMarkable(prev) && getMarkedState(prev.comment, prev.marked).isMarked;
    return (React.createElement(Divider, { className: st(classes.divider, {
            isMobile,
            noTopMargin: prevIsMarked,
            noBottomMargin: nextIsMarked,
        }) }));
};
CommentList.displayName = 'CommentList';
export default CommentList;
//# sourceMappingURL=comment-list.js.map